import React, { useState, MouseEvent } from "react";
import { GetSnapshotEventGroupedResponseItem } from "@/modules/snapshots/businessReports.types";

interface Props {
  groupedEvents: Record<string, Array<GetSnapshotEventGroupedResponseItem>>;
  getStatusColor: (status: string) => string;
}

export const StatusPieChart: React.FC<Props> = ({ groupedEvents, getStatusColor }) => {
  const [hoveredSegment, setHoveredSegment] = useState<number | null>(null);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });

  const events = Object.values(groupedEvents).flat();

  // Calculate status distribution
  const statusCounts = events.reduce<Record<string, number>>((acc, event) => {
    acc[event.status] = (acc[event.status] || 0) + 1;
    return acc;
  }, {});

  const totalEvents = events.length;
  const statusData = Object.entries(statusCounts).map(([status, count]) => ({
    status,
    count,
    percentage: totalEvents > 0 ? Math.round((count / totalEvents) * 100) : 0,
  }));

  const handleMouseEnter = (event: MouseEvent<SVGPathElement>, _segment: unknown, index: number) => {
    setHoveredSegment(index);
    const rect = (event.currentTarget as SVGPathElement).closest("svg")!.getBoundingClientRect();
    setTooltipPosition({
      x: event.clientX - rect.left + 10,
      y: event.clientY - rect.top - 10,
    });
  };

  const handleMouseLeave = () => setHoveredSegment(null);

  // Pie chart geometry
  const centerX = 120;
  const centerY = 120;
  const radius = 120;

  const polarToCartesian = (centerX: number, centerY: number, radius: number, angleInDegrees: number) => {
    const angleInRadians = ((angleInDegrees - 90) * Math.PI) / 180.0;
    return {
      x: centerX + radius * Math.cos(angleInRadians),
      y: centerY + radius * Math.sin(angleInRadians),
    };
  };

  const createPath = (startAngle: number, endAngle: number) => {
    const start = polarToCartesian(centerX, centerY, radius, endAngle);
    const end = polarToCartesian(centerX, centerY, radius, startAngle);
    const largeArcFlag = endAngle - startAngle <= 180 ? "0" : "1";
    return [
      "M",
      centerX,
      centerY,
      "L",
      start.x,
      start.y,
      "A",
      radius,
      radius,
      0,
      largeArcFlag,
      0,
      end.x,
      end.y,
      "Z",
    ].join(" ");
  };

  // Early return if no data
  if (!events || events.length === 0) {
    return (
      <div className="rounded-lg border border-[#e9e0d5] bg-white p-5 text-center text-muted-foreground">
        No data available
      </div>
    );
  }

  let currentAngle = 0;
  const segments = statusData.map(item => {
    const startAngle = currentAngle;
    const endAngle = currentAngle + item.percentage * 3.6 - 0.01; // 0.01 to avoid from 0 to 360
    currentAngle = endAngle;

    return {
      ...item,
      path: createPath(startAngle, endAngle),
      startAngle,
      endAngle,
    };
  });

  return (
    <div className="mion-h-[280px] relative flex w-full flex-col rounded-lg border border-[#e9e0d5] bg-white p-4 shadow-sm">
      {/* Header */}
      <div className="mb-4 text-center">
        <h3 className="m-0 text-base font-semibold text-[#0a213e]">Status Distribution</h3>
      </div>

      {/* Chart Container */}
      <div className="relative flex justify-center">
        <div className="relative">
          <svg width={240} height={240} style={{ overflow: "visible" }}>
            {/* Segments */}
            {segments.map((segment, index) => (
              <path
                key={segment.status}
                d={segment.path}
                fill={getStatusColor(segment.status)}
                stroke="white"
                strokeWidth={2}
                style={{
                  cursor: "pointer",
                  transition: "all 0.2s ease",
                  filter: hoveredSegment === index ? "brightness(1.1)" : "none",
                  transform: hoveredSegment === index ? "scale(1.02)" : "scale(1)",
                  transformOrigin: "120px 120px",
                }}
                onMouseEnter={e => handleMouseEnter(e, segment, index)}
                onMouseLeave={handleMouseLeave}
              />
            ))}

            {/* Center circle */}
            <circle cx={centerX} cy={centerY} r={35} fill="white" stroke="#e9e0d5" strokeWidth={1} />

            {/* Center text */}
            <text x={centerX} y={centerY - 5} textAnchor="middle" className="fill-[#0a213e] text-[20px] font-semibold">
              {totalEvents}
            </text>
            <text x={centerX} y={centerY + 15} textAnchor="middle" className="fill-[#8b92af] text-xs">
              Events
            </text>
          </svg>

          {/* Tooltip */}
          {hoveredSegment !== null && (
            <div
              className="pointer-events-none absolute z-50 whitespace-nowrap rounded-md border border-[#e9e0d5] bg-white px-3 py-2 text-sm shadow-lg"
              style={{
                left: tooltipPosition.x,
                top: tooltipPosition.y,
                fontSize: "13px",
              }}
            >
              <div className="mb-0.5 font-semibold text-[#0a213e]">{segments[hoveredSegment].status}</div>
              <div className="text-xs text-[#8b92af]">
                {segments[hoveredSegment].count} events ({segments[hoveredSegment].percentage}%)
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
